import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  TablePagination,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { printPaymentReceiptVouchersReport } from '../utils/printReports';

interface Voucher {
  _id: string;
  paymentNumber?: string;
  receiptNumber?: string;
  date: string;
  amount: number;
  description: string;
  status: string;
  voucherType: 'payment' | 'receipt';
  voucherTypeAr: string;
  debitAccount?: {
    _id: string;
    name: string;
    accountNumber: string;
  };
  creditAccount?: {
    _id: string;
    name: string;
    accountNumber: string;
  };
  branchId?: {
    _id: string;
    name: string;
  };
  createdBy?: {
    _id: string;
    username: string;
  };
}

interface Summary {
  totalAmount: number;
  paymentCount: number;
  receiptCount: number;
  totalCount: number;
  paymentSum: number;
  receiptSum: number;
}

interface Branch {
  _id: string;
  name: string;
}

const PaymentReceiptVouchersReportPage: React.FC = () => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    branchId: 'all',
    voucherType: 'all',
    status: 'all'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // جلب البيانات عند تحميل الصفحة
  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/api/branches');
      console.log('الفروع المحملة:', response.data);
      // API يرجع كائن يحتوي على branches
      const branchesData = response.data.branches || [];
      setBranches(branchesData);
    } catch (error) {
      console.error('خطأ في جلب الفروع:', error);
      setBranches([]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/reports/vouchers/payment-receipt-vouchers', {
        params: {
          ...filters,
          page: page + 1,
          limit: rowsPerPage
        }
      });

      setVouchers(response.data.vouchers);
      setSummary(response.data.summary);
      setTotalCount(response.data.pagination.totalItems);
    } catch (error: any) {
      console.error('خطأ في جلب البيانات:', error);
      setError(error.response?.data?.error || 'حدث خطأ أثناء جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchData();
  };

  const handleExport = async () => {
    try {
      const response = await axios.get('/api/reports/vouchers/payment-receipt-vouchers/export', {
        params: filters,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payment-receipt-vouchers-report-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('خطأ في تصدير التقرير:', error);
      setError('حدث خطأ أثناء تصدير التقرير');
    }
  };

  const handlePrint = () => {
    printPaymentReceiptVouchersReport(vouchers, summary, filters, branches);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'مؤكد':
        return 'success';
      case 'تحت الموافقة':
        return 'warning';
      case 'مرفوض':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: ar });
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            تقرير سندات القبض والصرف
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="تحديث">
              <IconButton onClick={fetchData} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={loading}
            >
              تصدير Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={loading}
            >
              طباعة
            </Button>
          </Box>
        </Box>

        {/* الفلاتر */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="من تاريخ"
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="إلى تاريخ"
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="الفرع"
              value={filters.branchId}
              onChange={(e) => handleFilterChange('branchId', e.target.value)}
            >
              <MenuItem value="all">الكل</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch._id} value={branch._id}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="نوع السند"
              value={filters.voucherType}
              onChange={(e) => handleFilterChange('voucherType', e.target.value)}
            >
              <MenuItem value="all">الكل</MenuItem>
              <MenuItem value="receipt">سند قبض</MenuItem>
              <MenuItem value="payment">سند صرف</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="حالة السند"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="all">الكل</MenuItem>
              <MenuItem value="مؤكد">مؤكد</MenuItem>
              <MenuItem value="تحت الموافقة">تحت الموافقة</MenuItem>
              <MenuItem value="مرفوض">مرفوض</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={loading}
              fullWidth
            >
              تطبيق الفلاتر
            </Button>
          </Grid>
        </Grid>

        {/* الإحصائيات */}
        {summary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      سندات القبض
                    </Typography>
                  </Box>
                  <Typography variant="h6">{summary.receiptCount}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(summary.receiptSum)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PaymentIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      سندات الصرف
                    </Typography>
                  </Box>
                  <Typography variant="h6">{summary.paymentCount}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(summary.paymentSum)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    إجمالي السندات
                  </Typography>
                  <Typography variant="h6">{summary.totalCount}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    المجموع الكلي
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(summary.totalAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* الجدول */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>رقم السند</TableCell>
                    <TableCell>نوع السند</TableCell>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>الفرع</TableCell>
                    <TableCell>الحساب المدين</TableCell>
                    <TableCell>الحساب الدائن</TableCell>
                    <TableCell align="right">المبلغ</TableCell>
                    <TableCell>الوصف</TableCell>
                    <TableCell>الحالة</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vouchers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        لا توجد بيانات
                      </TableCell>
                    </TableRow>
                  ) : (
                    vouchers.map((voucher) => (
                      <TableRow key={voucher._id}>
                        <TableCell>
                          {voucher.voucherType === 'payment'
                            ? voucher.paymentNumber
                            : voucher.receiptNumber}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={voucher.voucherTypeAr}
                            size="small"
                            color={voucher.voucherType === 'receipt' ? 'success' : 'error'}
                            icon={voucher.voucherType === 'receipt' ? <ReceiptIcon /> : <PaymentIcon />}
                          />
                        </TableCell>
                        <TableCell>{formatDate(voucher.date)}</TableCell>
                        <TableCell>{voucher.branchId?.name || '-'}</TableCell>
                        <TableCell>{voucher.debitAccount?.name || '-'}</TableCell>
                        <TableCell>{voucher.creditAccount?.name || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(voucher.amount)}</TableCell>
                        <TableCell>{voucher.description || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={voucher.status}
                            size="small"
                            color={getStatusColor(voucher.status)}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="عدد الصفوف:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`
              }
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentReceiptVouchersReportPage;
